import { accessByStation, accessRoles } from '../data/accessRoles';

/**
 * Gets the user's role (admin, pdi, or null)
 * @param {string} userName - The current user's name
 * @returns {string|null} - 'admin', 'pdi', or null
 */
export const getUserRole = (userName) => {
	if (!userName) return null;

	const adminRole = accessRoles.find(role => role.role === 'admin');
	if (adminRole?.names?.includes(userName)) {
		return 'admin';
	}

	const pdiRole = accessRoles.find(role => role.role === 'pdi');
	if (pdiRole?.names?.includes(userName)) {
		return 'pdi';
	}

	return null;
};

/**
 * Checks if a user has access to a specific station
 * @param {number} stationId - The station ID to check
 * @param {string} userName - The current user's name
 * @param {string} userRole - The user's role (admin, pdi, or null)
 * @returns {boolean} - true if user has access, false otherwise
 */
export const hasStationAccess = (stationId, userName, userRole) => {
	// Admin has access to everything (overrides all station restrictions)
	if (userRole === 'admin') {
		return true;
	}

	const stationConfig = accessByStation.find(s => s.station === stationId);

	// If station not found in config, deny access by default
	if (!stationConfig) {
		return false;
	}

	// If both names and roles are null, everyone has access
	if (stationConfig.names === null && stationConfig.roles === null) {
		return true;
	}

	// Check if user's role grants access to this station
	const hasRoleAccess = stationConfig.roles && userRole && stationConfig.roles.includes(userRole);

	// Check if user's name grants access to this station
	const hasNameAccess = stationConfig.names && stationConfig.names.includes(userName);

	// PDI role: access to everything EXCEPT stations with specific restrictions (where they're not listed)
	if (userRole === 'pdi') {
		// If station has specific names list and user is not in it, deny access
		if (stationConfig.names && !hasNameAccess && !hasRoleAccess) {
			return false;
		}
		// If station allows PDI role or user is in names list, grant access
		if (hasRoleAccess || hasNameAccess) {
			return true;
		}
		// If station has no restrictions or only role restrictions, PDI users can access
		if (stationConfig.names === null) {
			return true;
		}
		// Station has names list but user not in it
		return false;
	}

	// Regular users: can only access if their name is in the list OR their role is allowed
	return hasNameAccess || hasRoleAccess;
};

/**
 * Checks if a user has access to an item based on ANY of its stations
 * User needs access to at least ONE station in the item's stations array
 * @param {number[]} itemStations - Array of station IDs for the item
 * @param {string} userName - The current user's name
 * @param {string} userRole - The user's role
 * @returns {boolean} - true if user has access to at least ONE station
 */
export const hasItemAccess = (itemStations, userName, userRole) => {
	if (!itemStations || itemStations.length === 0) {
		return false;
	}

	// User needs access to at least ONE station in the array
	return itemStations.some(stationId =>
		hasStationAccess(stationId, userName, userRole)
	);
};

/**
 * Checks if a user has access to at least one item in a section
 * @param {Object} section - The section object with items
 * @param {string} userName - The current user's name
 * @param {string} userRole - The user's role
 * @returns {boolean} - true if user has access to at least one item
 */
export const hasSectionAccess = (section, userName, userRole) => {
	if (!section?.items || section.items.length === 0) {
		return false;
	}

	return section.items.some(item =>
		hasItemAccess(item.stations, userName, userRole)
	);
};

/**
 * Checks if a user has access to at least one section in a category
 * @param {Object} category - The category object with sections
 * @param {string} userName - The current user's name
 * @param {string} userRole - The user's role
 * @returns {boolean} - true if user has access to at least one section
 */
export const hasCategoryAccess = (category, userName, userRole) => {
	if (!category?.sections || category.sections.length === 0) {
		return false;
	}

	return category.sections.some(section =>
		hasSectionAccess(section, userName, userRole)
	);
};

/**
 * Filters items in a section based on user access
 * @param {Object} section - The section object with items
 * @param {string} userName - The current user's name
 * @param {string} userRole - The user's role
 * @returns {Object} - Section with filtered items
 */
export const filterSectionItems = (section, userName, userRole) => {
	if (!section) return section;

	return {
		...section,
		items: section.items?.filter(item =>
			hasItemAccess(item.stations, userName, userRole)
		) || []
	};
};

/**
 * Filters sections in a category based on user access
 * @param {Object} category - The category object with sections
 * @param {string} userName - The current user's name
 * @param {string} userRole - The user's role
 * @returns {Object} - Category with filtered sections
 */
export const filterCategorySections = (category, userName, userRole) => {
	if (!category) return category;

	return {
		...category,
		sections: category.sections?.map(section =>
			filterSectionItems(section, userName, userRole)
		).filter(section =>
			section.items && section.items.length > 0
		) || []
	};
};

/**
 * Filters all categories based on user access
 * @param {Array} categories - Array of category objects
 * @param {string} userName - The current user's name
 * @returns {Array} - Filtered array of categories
 */
export const filterCategories = (categories, userName) => {
	if (!categories || !userName) return categories;

	const userRole = getUserRole(userName);

	return categories
		.map(category => filterCategorySections(category, userName, userRole))
		.filter(category => hasCategoryAccess(category, userName, userRole));
};
